/**
 * Best-effort haptic feedback for the mobile player.
 *
 * - Android / Chrome: the Web Vibration API (navigator.vibrate) works directly.
 * - iOS Safari (including home-screen PWAs): Apple does NOT implement the Web
 *   Vibration API. The ONLY web-exposed haptic on iOS is a side effect of
 *   toggling an <input type="checkbox" switch> control (iOS 17.4+). We keep one
 *   hidden in the DOM and programmatically click it to fire the system tap.
 *
 * iOS requires the toggle to happen inside a user-gesture call stack — we call
 * tapHaptic() straight from the lane tap handler, so that holds. The iOS tap is
 * a single fixed-strength "selection" click; intensity can't be varied there.
 */

let labelEl: HTMLLabelElement | null = null;

function ensureSwitch(): void {
  if (labelEl || typeof document === 'undefined' || !document.body) return;
  const label = document.createElement('label');
  label.setAttribute('aria-hidden', 'true');
  // Off-screen but still RENDERED (display:none would disable the haptic).
  label.style.cssText =
    'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.setAttribute('switch', '');   // the attribute that triggers the iOS haptic
  input.tabIndex = -1;
  label.appendChild(input);
  document.body.appendChild(label);
  labelEl = label;
}

const supportsVibrate =
  typeof navigator !== 'undefined' && typeof (navigator as any).vibrate === 'function';

/** Create the hidden switch ahead of time (call once on game start). */
export function initHaptics(): void {
  try { ensureSwitch(); } catch { /* ignore */ }
}

/** Fire a short haptic. `strong` = a slightly longer buzz where supported. */
export function tapHaptic(strong = false): void {
  // Android / browsers that expose the Vibration API.
  if (supportsVibrate) {
    try { (navigator as any).vibrate(strong ? [0, 18] : [0, 10]); } catch { /* ignore */ }
  }
  // iOS switch-toggle haptic (silent no-op elsewhere / pre-17.4).
  try {
    ensureSwitch();
    labelEl?.click();
  } catch { /* ignore */ }
}
